import type { Camera, Material, Scene, WebGLRenderer, WebGLRenderTarget } from "three";
import { Color, MeshBasicMaterial, MultiplyBlending } from "three";
import { EffectComposerPass } from "../examples/jsm/postprocessing/EffectComposerPass";
import { FullScreenQuad } from "../examples/jsm/postprocessing/FullScreenQuad";
import { checkWebGLContextLost } from "./checkWebGLContextLost";
import type { RenderTargetPool } from "./RenderTargetPool";

/**
 * Renders selection highlights into an intermediate render target cleared to white, then blends
 * that target into the destination buffer with multiply blending. Multiplying (rather than drawing
 * the highlights directly into the destination) keeps whatever is already in the destination
 * visible, since unhighlighted areas stay white and act as a no-op.
 */
export class HighlightOverlayPass extends EffectComposerPass {
  scene: Scene | undefined;
  camera: Camera | undefined;
  overrideMaterial: Material;

  private fsQuad: FullScreenQuad;
  private fsQuadMaterial: MeshBasicMaterial;

  private oldClearColor = new Color();

  constructor(
    scene: Scene | undefined,
    camera: Camera | undefined,
    overrideMaterial: Material,
    private renderTargetPool: RenderTargetPool,
  ) {
    super();

    this.scene = scene;
    this.camera = camera;
    this.overrideMaterial = overrideMaterial;

    this.fsQuadMaterial = new MeshBasicMaterial({
      transparent: true,
      blending: MultiplyBlending,
      premultipliedAlpha: true, // Required for MultiplyBlending to work correctly.
      depthTest: false,
      depthWrite: false,
      name: "HighlightOverlayPass fsQuadMaterial",
    });
    this.fsQuad = new FullScreenQuad(this.fsQuadMaterial);
  }

  render(renderer: WebGLRenderer, writeBuffer: WebGLRenderTarget | null): void {
    if (!this.scene || !this.camera) {
      return;
    }

    const oldAutoClear = renderer.autoClear;
    renderer.autoClear = false;

    renderer.getClearColor(this.oldClearColor);
    const oldClearAlpha = renderer.getClearAlpha();
    renderer.setClearColor("white", 1.0);

    const oldBackground = this.scene.background;
    this.scene.background = null;
    this.scene.overrideMaterial = this.overrideMaterial;

    const intermediateBuffer = this.renderTargetPool.reserveRenderTargetMatching(writeBuffer, { depthBuffer: true });
    try {
      // Render into an intermediate buffer.
      // Rendering directly to target with a transparent material doesn't work,
      // since we want objects in front to occlude highlighted objects in the back.
      renderer.setRenderTarget(intermediateBuffer);
      renderer.clear(true, true, false);
      renderer.render(this.scene, this.camera);

      renderer.setRenderTarget(writeBuffer);
      this.fsQuadMaterial.map = intermediateBuffer.texture;
      this.fsQuad.render(renderer);
    } catch (e) {
      checkWebGLContextLost(renderer, e, "Rendering failed.");
    } finally {
      this.renderTargetPool.freeRenderTarget(intermediateBuffer);
    }

    this.scene.overrideMaterial = null;
    this.scene.background = oldBackground;
    renderer.setClearColor(this.oldClearColor, oldClearAlpha);
    renderer.autoClear = oldAutoClear;
  }
}
